import React, { useState, useEffect } from 'react';
import {
  TextField,
  Button,
  Typography,
  Container,
  Card,
  CardContent,
  IconButton,
  Paper,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Divider,
  Chip,
  Avatar,
  useMediaQuery,
  Alert,
  Tooltip
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  Delete,
  Edit,
  FilterAlt,
  Search,
  CalendarToday,
  Person,
  Home,
  Phone,
  Event,
  Comment,
  Add
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { addYears, differenceInCalendarDays, format, subDays } from 'date-fns';
import { useNavigate } from 'react-router-dom';

interface ExtinguisherData {
  id: number;
  name: string;
  date: string;
  count: number;
  address: string;
  dueDate: string;
  contactNumber: string;
  remarks: string;
  createdAt: string;
  updatedAt: string;
}

const formatStorageDate = (date: Date) => format(date, 'yyyy-MM-dd');

const calculateDueDate = (date: Date) => formatStorageDate(subDays(addYears(date, 1), 1));

const dayLabel = (days: number) => `${days} day${days === 1 ? '' : 's'}`;

const getDaysLeft = (dueDate: string) => {
  const parsedDueDate = new Date(dueDate);

  if (Number.isNaN(parsedDueDate.getTime())) {
    return null;
  }

  return differenceInCalendarDays(parsedDueDate, new Date());
};

const getDueDateStatus = (dueDate: string) => {
  const daysLeft = getDaysLeft(dueDate);

  if (daysLeft === null) {
    return { label: 'Invalid due date', color: 'default' as const };
  }

  if (daysLeft < 0) {
    return { label: `Overdue by ${dayLabel(Math.abs(daysLeft))}`, color: 'error' as const };
  }

  if (daysLeft === 0) {
    return { label: 'Due today', color: 'warning' as const };
  }

  if (daysLeft <= 30) {
    return { label: `Due in ${dayLabel(daysLeft)}`, color: 'warning' as const };
  }

  return { label: `${dayLabel(daysLeft)} left`, color: 'success' as const };
};

const getDefaultFormData = (): Omit<ExtinguisherData, 'id' | 'createdAt' | 'updatedAt'> => {
  const today = new Date();

  return {
    name: '',
    date: formatStorageDate(today),
    count: 1,
    address: '',
    dueDate: calculateDueDate(today),
    contactNumber: '',
    remarks: '',
  };
};

const DataForm: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();

  // Form state
  const [formData, setFormData] = useState<Omit<ExtinguisherData, 'id' | 'createdAt' | 'updatedAt'>>(getDefaultFormData);

  const [editId, setEditId] = useState<number | null>(null);
  const [dataList, setDataList] = useState<ExtinguisherData[]>([]);
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [summaryFilter, setSummaryFilter] = useState<'all' | 'dueSoon' | 'overdue'>('all');
  const currentYear = new Date().getFullYear().toString();
  const [filter, setFilter] = useState({
    search: '',
    month: '',
    year: currentYear,
  });
  const hasActiveFilters = Boolean(
    filter.search || filter.month || filter.year !== currentYear || summaryFilter !== 'all'
  );

  // Handle form field changes
  const handleChange = (field: keyof typeof formData) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [field]: e.target.value });
  };

  const handleDateChange = (date: Date | null) => {
    if (!date) return;

    setFormData((prev) => ({
      ...prev,
      date: formatStorageDate(date),
      dueDate: calculateDueDate(date),
    }));
  };

  // Form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const url = editId
      ? `https://mfs-api.midastix.com/newdata/${editId}`
      : 'https://mfs-api.midastix.com/newdata';

    const method = editId ? 'PUT' : 'POST';

    try {
      const parsedDate = new Date(formData.date);
      const payload = {
        ...formData,
        dueDate: Number.isNaN(parsedDate.getTime()) ? formData.dueDate : calculateDueDate(parsedDate),
      };

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        setMessage({
          text: `Data ${editId ? 'updated' : 'created'} successfully`,
          type: 'success'
        });
        fetchData();
        resetForm();
      } else {
        throw new Error('Server error');
      }
    } catch (error) {
      setMessage({
        text: `Failed to ${editId ? 'update' : 'create'} data. Please try again.`,
        type: 'error'
      });
    }
  };

  // Fetch data from API
  const fetchData = async () => {
    try {
      const response = await fetch('https://mfs-api.midastix.com/newdata');
      if (response.ok) {
        const result = await response.json();
        setDataList(result);
      } else {
        throw new Error('Failed to fetch data');
      }
    } catch (error) {
      setMessage({
        text: 'An error occurred while fetching the data.',
        type: 'error'
      });
    }
  };

  // Delete data
  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this record?')) return;

    try {
      const response = await fetch(`https://mfs-api.midastix.com/newdata/${id}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        setMessage({ text: 'Data deleted successfully.', type: 'success' });
        fetchData();
      } else {
        throw new Error('Failed to delete');
      }
    } catch (error) {
      setMessage({ text: 'An error occurred while deleting data.', type: 'error' });
    }
  };

  // Edit data
  const handleEdit = (data: ExtinguisherData) => {
    const parsedDate = new Date(data.date);
    const calculatedDueDate = Number.isNaN(parsedDate.getTime()) ? data.dueDate : calculateDueDate(parsedDate);

    setFormData({
      name: data.name,
      date: data.date,
      count: data.count,
      address: data.address,
      dueDate: calculatedDueDate,
      contactNumber: data.contactNumber,
      remarks: data.remarks,
    });
    setEditId(data.id);
    setOpenDialog(true);
  };

  // Reset form
  const resetForm = () => {
    setFormData(getDefaultFormData());
    setEditId(null);
    setOpenDialog(false);
  };

  const resetFilters = () => {
    setFilter({
      search: '',
      month: '',
      year: currentYear,
    });
    setSummaryFilter('all');
  };

  // Apply filters
  const baseFilteredData = dataList.filter(item => {
    const matchesSearch = item.name.toLowerCase().includes(filter.search.toLowerCase()) ||
      item.address.toLowerCase().includes(filter.search.toLowerCase());

    const itemDate = new Date(item.date);
    const matchesMonth = filter.month ? itemDate.getMonth() + 1 === parseInt(filter.month) : true;
    const matchesYear = itemDate.getFullYear().toString() === filter.year;

    return matchesSearch && matchesMonth && matchesYear;
  });

  const dueSoonCount = baseFilteredData.filter((item) => {
    if (!item.dueDate) return false;
    const daysLeft = getDaysLeft(item.dueDate);
    if (daysLeft === null) return false;
    return daysLeft >= 0 && daysLeft <= 30;
  }).length;
  const overdueCount = baseFilteredData.filter((item) => {
    if (!item.dueDate) return false;
    const daysLeft = getDaysLeft(item.dueDate);
    if (daysLeft === null) return false;
    return daysLeft < 0;
  }).length;
  const filteredData = baseFilteredData.filter((item) => {
    if (summaryFilter === 'all') return true;
    if (!item.dueDate) return false;

    const daysLeft = getDaysLeft(item.dueDate);
    if (daysLeft === null) return false;

    if (summaryFilter === 'dueSoon') return daysLeft >= 0 && daysLeft <= 30;
    return daysLeft < 0;
  });
  const totalUnits = filteredData.reduce((total, item) => total + Number(item.count || 0), 0);
  const summaryFilterLabel =
    summaryFilter === 'dueSoon' ? 'Due In 30 Days' : summaryFilter === 'overdue' ? 'Overdue' : 'All';
  const emptyStateTitle =
    summaryFilter === 'all' ? 'No records match your filters' : `No ${summaryFilterLabel.toLowerCase()} records found`;

  useEffect(() => {
    fetchData();
  }, []);

  // Close message after 3 seconds
  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => setMessage(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Header */}
        <Paper
          elevation={0}
          sx={{
            mb: 3,
            p: { xs: 2.5, md: 3.5 },
            borderRadius: 3,
            position: 'relative',
            overflow: 'hidden',
            color: 'common.white',
            background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 65%, ${theme.palette.secondary.main} 100%)`,
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              width: 180,
              height: 180,
              borderRadius: '50%',
              top: -90,
              right: -40,
              backgroundColor: alpha('#ffffff', 0.15),
            }}
          />
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: { xs: 'flex-start', sm: 'center' },
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 1.5,
              }}
            >
              <Typography variant={isMobile ? 'h5' : 'h4'} component="h1" gutterBottom sx={{ fontWeight: 700, mb: 0 }}>
                Fire Extinguisher Management
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  gap: 1,
                  width: { xs: '100%', sm: 'auto' },
                  justifyContent: { xs: 'flex-start', sm: 'flex-end' },
                }}
              >
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => navigate('/todoapp')}
                  sx={{
                    bgcolor: alpha('#ffffff', 0.2),
                    color: 'common.white',
                    border: `1px solid ${alpha('#ffffff', 0.35)}`,
                    '&:hover': {
                      bgcolor: alpha('#ffffff', 0.3),
                    },
                  }}
                >
                  Todo App
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => navigate('/accounts')}
                  sx={{
                    bgcolor: alpha('#ffffff', 0.2),
                    color: 'common.white',
                    border: `1px solid ${alpha('#ffffff', 0.35)}`,
                    '&:hover': {
                      bgcolor: alpha('#ffffff', 0.3),
                    },
                  }}
                >
                  Accounts
                </Button>
              </Box>
            </Box>
            <Typography variant="body1" sx={{ opacity: 0.92, maxWidth: 620 }}>
              Track inspections, due dates, and customer records from one dashboard.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2 }}>
              <Chip label={`${filteredData.length} Records`} sx={{ bgcolor: alpha('#ffffff', 0.2), color: 'common.white' }} />
              <Chip label={`${totalUnits} Total Units`} sx={{ bgcolor: alpha('#ffffff', 0.2), color: 'common.white' }} />
              <Chip label={`${dueSoonCount} Due Soon`} sx={{ bgcolor: alpha('#ffffff', 0.2), color: 'common.white' }} />
              <Chip label={`${overdueCount} Overdue`} sx={{ bgcolor: alpha('#ffffff', 0.2), color: 'common.white' }} />
            </Box>
          </Box>
        </Paper>

        {/* Summary Section */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)',
            gap: 2,
            mb: 3,
          }}
        >
          <Paper elevation={0} sx={{ p: 2, borderRadius: 2.5, border: `1px solid ${alpha(theme.palette.divider, 0.8)}` }}>
            <Typography variant="caption" color="text.secondary">Filtered Records</Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.5 }}>{filteredData.length}</Typography>
          </Paper>
          <Paper elevation={0} sx={{ p: 2, borderRadius: 2.5, border: `1px solid ${alpha(theme.palette.divider, 0.8)}` }}>
            <Typography variant="caption" color="text.secondary">Total Extinguishers</Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.5 }}>{totalUnits}</Typography>
          </Paper>
          <Paper
            elevation={0}
            onClick={() => setSummaryFilter((prev) => (prev === 'dueSoon' ? 'all' : 'dueSoon'))}
            sx={{
              p: 2,
              borderRadius: 2.5,
              border: `1px solid ${alpha(theme.palette.warning.main, summaryFilter === 'dueSoon' ? 0.65 : 0.25)}`,
              bgcolor: alpha(theme.palette.warning.main, summaryFilter === 'dueSoon' ? 0.18 : 0.07),
              cursor: 'pointer',
              transition: 'all 160ms ease',
              boxShadow: summaryFilter === 'dueSoon' ? `0 0 0 2px ${alpha(theme.palette.warning.main, 0.2)}` : 'none',
            }}
          >
            <Typography variant="caption" color="text.secondary">Due In 30 Days</Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.5, color: 'warning.dark' }}>{dueSoonCount}</Typography>
          </Paper>
          <Paper
            elevation={0}
            onClick={() => setSummaryFilter((prev) => (prev === 'overdue' ? 'all' : 'overdue'))}
            sx={{
              p: 2,
              borderRadius: 2.5,
              border: `1px solid ${alpha(theme.palette.error.main, summaryFilter === 'overdue' ? 0.65 : 0.25)}`,
              bgcolor: alpha(theme.palette.error.main, summaryFilter === 'overdue' ? 0.16 : 0.07),
              cursor: 'pointer',
              transition: 'all 160ms ease',
              boxShadow: summaryFilter === 'overdue' ? `0 0 0 2px ${alpha(theme.palette.error.main, 0.22)}` : 'none',
            }}
          >
            <Typography variant="caption" color="text.secondary">Overdue</Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.5, color: 'error.main' }}>{overdueCount}</Typography>
          </Paper>
        </Box>

        {/* Filter Section */}
        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            mb: 3,
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
            boxShadow: `0 10px 28px ${alpha(theme.palette.common.black, 0.06)}`,
          }}
        >
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <FilterAlt sx={{ mr: 1 }} /> Filters
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr 1fr auto auto',
              gap: 1.5,
              alignItems: 'center',
            }}
          >
            <TextField
              fullWidth
              label="Search by name or address"
              variant="outlined"
              size="small"
              value={filter.search}
              onChange={(e) => setFilter({ ...filter, search: e.target.value })}
              InputProps={{
                startAdornment: <Search sx={{ mr: 1, color: 'action.active' }} />,
              }}
            />

            <FormControl fullWidth size="small">
              <InputLabel>Month</InputLabel>
              <Select
                value={filter.month}
                label="Month"
                onChange={(e) => setFilter({ ...filter, month: e.target.value as string })}
              >
                <MenuItem value="">All Months</MenuItem>
                {Array.from({ length: 12 }, (_, i) => (
                  <MenuItem key={i + 1} value={i + 1}>
                    {new Date(0, i).toLocaleString('default', { month: 'long' })}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth size="small">
              <InputLabel>Year</InputLabel>
              <Select
                value={filter.year}
                label="Year"
                onChange={(e) => setFilter({ ...filter, year: e.target.value as string })}
              >
                {Array.from({ length: 5 }, (_, i) => {
                  const year = new Date().getFullYear() - i;
                  return (
                    <MenuItem key={year} value={year.toString()}>
                      {year}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>

            <Button
              variant="outlined"
              color="inherit"
              onClick={resetFilters}
              disabled={!hasActiveFilters}
              fullWidth={isMobile}
              sx={{ height: 40, minWidth: isMobile ? 'auto' : 120 }}
            >
              Reset
            </Button>

            <Button
              variant="contained"
              color="primary"
              onClick={() => setOpenDialog(true)}
              fullWidth={isMobile}
              startIcon={<Add />}
              sx={{ height: 40, minWidth: isMobile ? 'auto' : 150 }}
            >
              {isMobile ? 'Add' : 'Add Record'}
            </Button>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            Showing {filteredData.length} of {baseFilteredData.length} records ({summaryFilterLabel} view)
          </Typography>
        </Paper>

        {/* Status Message */}
        {message && (
          <Alert severity={message.type} sx={{ mb: 3, borderRadius: 2 }}>
            {message.text}
          </Alert>
        )}

        {/* Data List */}
        {filteredData.length > 0 ? (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(360px, 1fr))',
              gap: 2.5,
            }}
          >
            {filteredData.map((data) => {
              const dueStatus = data.dueDate ? getDueDateStatus(data.dueDate) : null;

              return (
                <Card
                  key={data.id}
                  sx={{
                    borderRadius: 3,
                    border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
                    boxShadow: `0 8px 24px ${alpha(theme.palette.common.black, 0.05)}`,
                    height: '100%',
                    display: 'flex',
                    transition: 'transform 180ms ease, box-shadow 180ms ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: `0 14px 30px ${alpha(theme.palette.common.black, 0.12)}`,
                    },
                  }}
                >
                  <CardContent sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                      <Chip
                        label={`Qty: ${data.count}`}
                        color="primary"
                        size="small"
                        avatar={<Avatar sx={{ bgcolor: theme.palette.primary.dark }}>{data.count}</Avatar>}
                      />
                      <Box>
                        <Tooltip title="Edit record">
                          <IconButton
                            onClick={() => handleEdit(data)}
                            size="small"
                            color="primary"
                            sx={{ '&:hover': { bgcolor: theme.palette.primary.light } }}
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete record">
                          <IconButton
                            onClick={() => handleDelete(data.id)}
                            size="small"
                            color="error"
                            sx={{ '&:hover': { bgcolor: theme.palette.error.light } }}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>

                    <Typography
                      variant="h6"
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        fontWeight: 700,
                        minHeight: 52,
                        overflow: 'hidden',
                        lineHeight: 1.3,
                      }}
                    >
                      <Person sx={{ mr: 1, mt: 0.2, color: theme.palette.primary.main }} />
                      <Box
                        component="span"
                        sx={{
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          wordBreak: 'break-word',
                        }}
                      >
                        {data.name}
                      </Box>
                    </Typography>

                    <Box
                      sx={{
                        mt: 1,
                        mb: 1.5,
                        minHeight: 44,
                        display: 'flex',
                        alignItems: 'flex-start',
                      }}
                    >
                      <Home sx={{ mr: 1, mt: 0.2, color: theme.palette.secondary.main }} fontSize="small" />
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}
                      >
                        {data.address}
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 1,
                        mb: 1.5,
                        minHeight: 64,
                        alignContent: 'flex-start',
                      }}
                    >
                      <Chip
                        size="small"
                        variant="outlined"
                        icon={<CalendarToday fontSize="small" />}
                        label={`Date: ${format(new Date(data.date), 'dd-MM-yyyy')}`}
                      />
                      {data.dueDate && (
                        <Chip
                          size="small"
                          variant="outlined"
                          icon={<Event fontSize="small" />}
                          label={`Due: ${format(new Date(data.dueDate), 'dd-MM-yyyy')}`}
                        />
                      )}
                      {dueStatus && (
                        <Chip
                          size="small"
                          label={dueStatus.label}
                          color={dueStatus.color}
                          sx={{ fontWeight: 600 }}
                        />
                      )}
                    </Box>

                    <Box sx={{ mt: 'auto' }}>
                      {data.contactNumber ? (
                        <Typography
                          component="a"
                          href={`tel:${data.contactNumber}`}
                          variant="body2"
                          sx={{
                            mb: 1.5,
                            display: 'inline-flex',
                            alignItems: 'center',
                            color: 'text.secondary',
                            textDecoration: 'none',
                            minHeight: 24,
                            '&:hover': { color: 'primary.main', textDecoration: 'underline' },
                          }}
                        >
                          <Phone sx={{ mr: 1 }} fontSize="small" />
                          {data.contactNumber}
                        </Typography>
                      ) : (
                        <Box sx={{ minHeight: 24, mb: 1.5 }} />
                      )}

                      <Divider sx={{ my: 1.5 }} />
                      {data.remarks ? (
                        <Box
                          sx={{
                            minHeight: 42,
                            display: 'flex',
                            alignItems: 'flex-start',
                          }}
                        >
                          <Comment sx={{ mr: 1, mt: 0.3 }} fontSize="small" />
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                            }}
                          >
                            {data.remarks}
                          </Typography>
                        </Box>
                      ) : (
                        <Box sx={{ minHeight: 42 }} />
                      )}
                    </Box>
                  </CardContent>
                </Card>
              );
            })}
          </Box>
        ) : (
          <Paper
            elevation={0}
            sx={{
              p: 5,
              textAlign: 'center',
              borderRadius: 3,
              border: `1px dashed ${alpha(theme.palette.divider, 0.9)}`,
            }}
          >
            <Event sx={{ fontSize: 46, color: 'text.disabled', mb: 1 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {emptyStateTitle}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Try resetting filters or add a new extinguisher record.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              sx={{ mt: 2 }}
              onClick={() => setOpenDialog(true)}
              startIcon={<Add />}
            >
              Add New Extinguisher
            </Button>
          </Paper>
        )}

        {/* Add/Edit Dialog */}
        <Dialog
          open={openDialog}
          onClose={resetForm}
          maxWidth="sm"
          fullWidth
          fullScreen={isMobile}
          PaperProps={{ sx: { borderRadius: isMobile ? 0 : 3, overflow: 'hidden' } }}
        >
          <DialogTitle sx={{ bgcolor: theme.palette.primary.main, color: 'white', pb: 1.5 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {editId ? 'Edit Extinguisher' : 'Add New Extinguisher'}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              Due date is auto-calculated as one day before the next year.
            </Typography>
          </DialogTitle>
          <DialogContent>
            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 2 }}>
                <TextField
                  label="Name"
                  fullWidth
                  variant="outlined"
                  size="small"
                  margin="normal"
                  value={formData.name}
                  onChange={handleChange('name')}
                  required
                  InputProps={{
                    startAdornment: <Person sx={{ mr: 1, color: 'action.active' }} />,
                  }}
                />

                <DatePicker
                  label="Date"
                  value={new Date(formData.date)}
                  onChange={handleDateChange}
                  format="dd-MM-yyyy"
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      required: true,
                      size: 'small',
                      margin: 'normal'
                    }
                  }}
                />

                <TextField
                  label="Count"
                  fullWidth
                  variant="outlined"
                  size="small"
                  margin="normal"
                  value={formData.count}
                  onChange={handleChange('count')}
                  required
                // inputProps={{ min: 1 }}
                />

                <TextField
                  label="Address"
                  fullWidth
                  variant="outlined"
                  size="small"
                  margin="normal"
                  value={formData.address}
                  onChange={handleChange('address')}
                  required
                  InputProps={{
                    startAdornment: <Home sx={{ mr: 1, color: 'action.active' }} />,
                  }}
                />

                <DatePicker
                  label="Due Date"
                  value={formData.dueDate ? new Date(formData.dueDate) : null}
                  format="dd-MM-yyyy"
                  disabled
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: 'small',
                      margin: 'normal',
                      helperText: 'Automatically calculated from Date'
                    }
                  }}
                />

                <TextField
                  label="Contact Number (Optional)"
                  fullWidth
                  variant="outlined"
                  size="small"
                  margin="normal"
                  value={formData.contactNumber}
                  onChange={handleChange('contactNumber')}
                  InputProps={{
                    startAdornment: <Phone sx={{ mr: 1, color: 'action.active' }} />,
                  }}
                />
              </Box>

              <TextField
                label="Remarks (Optional)"
                fullWidth
                multiline
                rows={3}
                variant="outlined"
                size="small"
                margin="normal"
                sx={{ mt: 2 }}
                value={formData.remarks}
                onChange={handleChange('remarks')}
                InputProps={{
                  startAdornment: <Comment sx={{ mr: 1, color: 'action.active', alignSelf: 'flex-start', mt: 1 }} />,
                }}
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={resetForm} variant="outlined">Cancel</Button>
            <Button onClick={handleSubmit} variant="contained" color="primary" startIcon={editId ? <Edit /> : <Add />}>
              {editId ? 'Update' : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </LocalizationProvider>
  );
};

export default DataForm;
